import { Box, Drawer, Modal } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import { connect } from "react-redux";
import Sidebar from "./components/sidebar";
import Header from "./components/header";
import { parseCSVMax, rowsToCSV } from "../util";
import axios from "axios";

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-20%`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
      maxWidth: "80%",
    }),
  }),
);

const BulkUploadSubmission = ({ role, token }) => {
  const [openSidebar, setOpenSidebar] = useState(true);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invalidEntries, setInvalidEntries] = useState();
  const [validEntries, setValidEntries] = useState();
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadedEntries, setUploadedEntries] = useState();

  // const temp = {
  //     hcpId: hcp.hcp.id,
  //     patientId: patient.patient.id,
  //     productId: product.productId,
  //     status: "Approved",
  //     quantity: inspraCapsules50mgQuantity
  // }

  const REQUIRED_HEADERS = [
    "hcpEmail",
    "patientPhoneNumber",
    "patientNrIc",
    "medicationName",
    "sku",
    "quantity",
  ];

  const labelForTemplate = REQUIRED_HEADERS.map((item) => [item, item]);

  const labelforCSV = [
    ["HCP Name", "hcpName"],
    ["HCP Email", "hcpEmail"],
    ["Patient Phone Number", "patientPhoneNumber"],
    ["Patient IC Number", "patientNrIc"],
    ["Medication Name", "medicationName"],
    ["Product SKU", "sku"],
    ["Quantity", "quantity"],
    ["Error", "error"],
    ["HCP Id", "hcpId"],
    ["Patient Id", "patientId"],
    ["Product Id", "productId"],
    ["Brand Id", "brandId"],
    ["Exceed HCP Limit", "hcpExceedLimit"],
  ];

  const labelforCSVUploadEntries = [
    ["HCP Name", "hcpName"],
    ["HCP Email", "hcpEmail"],
    ["Patient Phone Number", "patientPhoneNumber"],
    ["Patient IC Number", "patientNrIc"],
    ["Medication Name", "medicationName"],
    ["Product SKU", "sku"],
    ["Quantity", "quantity"],
    ["Error", "error"],
    ["Created Submission", "createdSubmission"],
    ["Created Redemption", "createdRedemption"],
    ["Exceed Limit Max Purchase Quantity", "exceedLimitMaxPurchaseQuantity"],
    ["Got Stock", "gotStock"],
    ["HCP Exceed Limit", "hcpExceedLimit"],
  ];

  const resetData = (type) => {
    switch (type) {
      case "fileUpload":
        setInvalidEntries();
        setValidEntries();
        setFile();
        setUploadedEntries();
        break;
      case "checkData":
        setInvalidEntries();
        setValidEntries();
        setUploadedEntries();
        break;
      default:
        setInvalidEntries();
        setValidEntries();
        setFile();
        setUploadedEntries();
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    resetData("fileUpload");
    const file = e.target.files?.[0];

    if (!file || !file.name.endsWith(".csv")) {
      alert("Please upload a valid CSV file.");
      setLoading(false);
      return;
    }

    try {
      const text = await file.text();

      // Use improved CSV parsing that handles multiline content
      const rows = parseCSVMax(text);
      const headers = rows[0].map((h) => h.trim());

      if (headers?.length > REQUIRED_HEADERS?.length) {
        alert(
          `Extra columns is detected: ${headers?.length}. Function expected only ${REQUIRED_HEADERS?.length} columns. Please follow the provided template`,
        );
        return;
      }
      const missingHeaders = REQUIRED_HEADERS.filter(
        (h) => !headers.includes(h),
      );
      if (missingHeaders.length > 0) {
        alert(`Missing required columns: ${missingHeaders.join(", ")}`);
        return;
      }
      const csvString = rowsToCSV(rows);

      const newCSV = new File([csvString], "processed.csv", {
        type: "text/csv",
      });

      setFile(newCSV);
    } catch (error) {
      alert("Failed to upload file");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckSubmissionEntries = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      resetData("checkData");
      if (!file) {
        throw new Error("Missing required file to check");
      }
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(
        "/api/admin/bulkUploadSubmission/check",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      const result = data?.result;

      const invalidEntries = [];
      const validEntries = [];
      result.forEach((item) => {
        const validCondition =
          item?.hcpId &&
          item?.patientId &&
          item?.productId &&
          item?.brandId &&
          !item?.hcpExceedLimit;

        if (validCondition) {
          validEntries.push(item);
        } else {
          const checkHcpExceedLimitShowError =
            item?.hcpId && item?.patientId && item?.productId && item?.brandId
              ? item?.hcpExceedLimit
                ? true
                : false
              : false;

          const error = [
            !item?.hcpId && "HCP not found",
            !item?.patientId && "Patient not found",
            !item?.productId && "Product not found",
            !item?.brandId && "Brand not found",
            checkHcpExceedLimitShowError && "Exceed the HCP Limit (3)",
          ].filter(Boolean);
          invalidEntries.push({ ...item, error });
        }
      });
      setInvalidEntries(invalidEntries);
      setValidEntries(validEntries);
    } catch (error) {
      alert(error);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowConfirmButton = (e) => {
    e.preventDefault();
    if (!validEntries) {
      alert("Missing valid entries in the checked file to be submit");
    } else {
      setShowConfirm(true);
    }
  };

  const handleBulkCreateSubmission = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!validEntries) {
        throw new Error(
          "Missing valid entries in the checked file to be submit",
        );
      }

      const formData = new FormData();
      formData.append("filename", file.name);
      formData.append("checkData", JSON.stringify(validEntries));
      const res = await axios.post(
        "/api/admin/bulkUploadSubmission",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      const rawResult = res?.data?.result ?? [];
      const rawResultWithError = [];
      rawResult.forEach((item) => {
        const error = [
          !item?.createdSubmission?.id && "Submission is not created",
          item?.exceedLimitMaxPurchaseQuantity &&
            "Exceed the limit of max purchase quantity",
          !item?.gotStock && "No Stock Available",
          item?.hcpExceedLimit && "Exceed the HCP Limit (3)",
        ].filter(Boolean);
        rawResultWithError.push({ ...item, error });
      });
      setUploadedEntries(rawResultWithError);
    } catch (error) {
      alert(error);
      console.error(error);
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    const escaped = stringValue.replace(/"/g, '""');
    return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
  };

  const downloadCSVValuesOnly = (filename, dataToExport, labelforCSVInput) => {
    if (!filename || !dataToExport || !labelforCSV) {
      alert("Missing required arguments");
    }

    if (!dataToExport?.length && filename !== "template.csv") {
      alert("No data available to download");
      return;
    }

    const headers = labelforCSVInput
      .map(([label]) => escapeCSV(label))
      .join(",");

    const csvRows = dataToExport.map((row) =>
      labelforCSVInput
        .map(([, key]) => {
          let value = "";

          const rowKey = key;
          if (rowKey === "createdSubmission") {
            value = row?.createdSubmission?.id;
          } else if (rowKey === "createdRedemption") {
            value = row?.createdRedemptions?.map((item) => item?.id).join(", ");
          } else {
            const rawValue = row[rowKey];
            value =
              rawValue && typeof rawValue === "object" ? rawValue : rawValue;
          }

          return escapeCSV(value);
        })
        .join(","),
    );

    const csvContent = [headers, ...csvRows].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="d-flex">
      <Drawer
        sx={{
          minWidth: "20%",
          "& .MuiDrawer-paper": {
            minWidth: "20%",
            boxSizing: "border-box",
            minHeight: "100vh",
            backgroundColor: "#FE7072",
          },
        }}
        anchor="left"
        open={openSidebar}
        variant="persistent"
      >
        <Sidebar />
      </Drawer>
      <Main open={openSidebar}>
        <div className="mt-3 px-3">
          <Header
            title="BULK UPLOAD SUBMISSION"
            openSidebar={openSidebar}
            setOpenSidebar={setOpenSidebar}
          />
          <div className="report-header mt-3 d-flex py-3 px-3 justify-content-center">
            {/* Beautiful Registration Type Selection */}
            <div className="w-100 gray-background-1">
              <h3
                style={{
                  margin: "0 0 16px 0",
                  color: "#1976d2",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                📋 Step 1: Download the template file
              </h3>
              <p>
                Please follow the provided template exactly. Do not add extra
                columns or modify the existing structure. Avoid using commas in
                any field values, as this may cause issues during file
                processing.
              </p>

              <div className="mt-2 d-flex justify-content-end align-items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    downloadCSVValuesOnly("template.csv", [], labelForTemplate)
                  }
                  disabled={loading}
                  className={`check-tab-button d-flex justify-content-center align-items-center`}
                >
                  {loading ? (
                    <div
                      className={`${loading ? "spinner-loading-btn" : ""}`}
                    ></div>
                  ) : (
                    "Download CSV Template"
                  )}
                </button>
              </div>

              <h3
                style={{
                  margin: "0 0 16px 0",
                  color: "#1976d2",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                📋 Step 2: Check the validity of your input file
              </h3>

              <form
                onSubmit={handleCheckSubmissionEntries}
                encType="multipart/form-data"
                className="w-100 d-flex flex-column items-end gap-2"
              >
                <input
                  type="file"
                  name="file"
                  accept=".csv"
                  required
                  className="w-100 border border-gray-300 rounded px-2 py-1"
                  onChange={handleFileUpload}
                />
                <button
                  type="submit"
                  disabled={loading || !file}
                  className={`check-tab-button d-flex justify-content-center align-items-center align-self-end`}
                >
                  {loading ? (
                    <div
                      className={`${loading ? "spinner-loading-btn" : ""}`}
                    ></div>
                  ) : (
                    "Check Validity"
                  )}
                </button>
              </form>

              <h3
                style={{
                  margin: "0 0 16px 0",
                  color: "black",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
                className="mt-4"
              >
                List of Invalid Users:{" "}
                <span className="text-danger">{invalidEntries?.length}</span>
              </h3>
              <div
                className="d-grid mt-1 overflow-auto"
                style={{
                  gridTemplateColumns: `repeat(${labelforCSV?.length ?? "12"}, 1fr)`,
                }}
              >
                {/* Header */}
                {labelforCSV?.map((item, index) => {
                  return (
                    <div className="check-tab-row-header" key={index}>
                      {item[1]}
                    </div>
                  );
                })}

                {/* Rows */}
                {invalidEntries ? (
                  invalidEntries?.map((item, index) => {
                    return (
                      <React.Fragment key={index}>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.hcpName}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.hcpEmail}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.patientPhoneNumber}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.patientNrIc}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.medicationName}
                        </div>
                        <div
                          className={`check-tab-row-extra-big ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.sku}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.quantity}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          <ul>
                            {item?.error &&
                              item?.error.map((item, index) => (
                                <li
                                  className="text-danger"
                                  style={{ width: "150px" }}
                                  key={index}
                                >
                                  {item}
                                </li>
                              ))}
                          </ul>
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.hcpId}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.patientId}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.productId}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.brandId}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.patientId && item?.productId && item?.brandId
                            ? item?.hcpExceedLimit
                              ? "TRUE"
                              : "FALSE"
                            : "NOT CHECKED DUE TO OTHER ISSUES TAKE PRIORITY"}
                        </div>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <React.Fragment>
                    {labelforCSV?.map((item, index) => {
                      return <div className="check-tab-row" key={index}></div>;
                    })}
                  </React.Fragment>
                )}
              </div>

              <div className="mt-2 d-flex justify-content-end align-items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    downloadCSVValuesOnly(
                      "invalid_entries.csv",
                      invalidEntries,
                      labelforCSV,
                    )
                  }
                  disabled={loading}
                  className={`check-tab-button d-flex justify-content-center align-items-center`}
                >
                  {loading ? (
                    <div
                      className={`${loading ? "spinner-loading-btn" : ""}`}
                    ></div>
                  ) : (
                    "Download Invalid Users as CSV"
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleShowConfirmButton}
                  disabled={loading}
                  className={`check-tab-button d-flex justify-content-center align-items-center`}
                >
                  {loading ? (
                    <div
                      className={`${loading ? "spinner-loading-btn" : ""}`}
                    ></div>
                  ) : (
                    "Bulk Upload"
                  )}
                </button>
              </div>

              <h3
                style={{
                  margin: "0 0 16px 0",
                  color: "black",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
                className="mt-4"
              >
                List of Uploaded Users:{" "}
                <span className="text-black">{uploadedEntries?.length}</span>
              </h3>
              <div
                className="d-grid mt-1 overflow-auto"
                style={{
                  gridTemplateColumns: `repeat(${labelforCSVUploadEntries?.length ?? "13"}, 1fr)`,
                }}
              >
                {/* Header */}
                {labelforCSVUploadEntries?.map((item, index) => {
                  return (
                    <div className="check-tab-row-header" key={index}>
                      {item[1]}
                    </div>
                  );
                })}

                {/* Rows */}
                {uploadedEntries ? (
                  uploadedEntries?.map((item, index) => {
                    return (
                      <React.Fragment key={index}>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.hcpName}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.hcpEmail}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.patientPhoneNumber}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.patientNrIc}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.medicationName}
                        </div>
                        <div
                          className={`check-tab-row-extra-big ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.sku}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.quantity}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          <ul>
                            {item?.error &&
                              item?.error?.map((item, index) => (
                                <li
                                  className="text-danger"
                                  style={{ width: "150px" }}
                                  key={index}
                                >
                                  {item}
                                </li>
                              ))}
                          </ul>
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.createdSubmission?.id}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          <ul>
                            {item?.createdRedemptions &&
                              item?.createdRedemptions?.map((item, key) => (
                                <li index={key}>{item?.id}</li>
                              ))}
                          </ul>
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.exceedLimitMaxPurchaseQuantity
                            ? "TRUE"
                            : "FALSE"}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.gotStock ? "TRUE" : "FALSE"}
                        </div>
                        <div
                          className={`check-tab-row ${index % 2 !== 0 ? "odd-row" : "even-row"}`}
                        >
                          {item?.hcpExceedLimit ? "TRUE" : "FALSE"}
                        </div>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <React.Fragment>
                    {labelforCSVUploadEntries?.map((item, index) => {
                      return <div className="check-tab-row" key={index}></div>;
                    })}
                  </React.Fragment>
                )}
              </div>
              <div className="mt-2 d-flex justify-content-end align-items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    downloadCSVValuesOnly(
                      "uploaded_entries.csv",
                      uploadedEntries,
                      labelforCSVUploadEntries,
                    )
                  }
                  disabled={loading}
                  className={`check-tab-button d-flex justify-content-center align-items-center`}
                >
                  {loading ? (
                    <div
                      className={`${loading ? "spinner-loading-btn" : ""}`}
                    ></div>
                  ) : (
                    "Download Uploaded Users as CSV"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        <Modal open={showConfirm}>
          <Box className="confirmation-modal">
            <h3 className="text-center">Confirm Bulk Upload Submission?</h3>

            <div>
              <p className="text-center">
                You're about to upload multiple records at once. Please review
                the invalid data carefully. Invalid entries will not be included
                in this upload
              </p>
            </div>

            <div className="d-flex justify-content-center">
              <button
                type="button"
                className="btn btn-danger px-6 py-2"
                disabled={loading}
                onClick={() => setShowConfirm(false)}
              >
                {loading ? (
                  <div
                    className={`${loading ? "spinner-loading-btn" : ""}`}
                  ></div>
                ) : (
                  "Cancel"
                )}
              </button>
              <button
                type="button"
                className="btn btn-primary ms-4 px-6 py-2"
                disabled={loading}
                onClick={handleBulkCreateSubmission}
              >
                {" "}
                {loading ? (
                  <div
                    className={`${loading ? "spinner-loading-btn" : ""}`}
                  ></div>
                ) : (
                  "Proceed"
                )}
              </button>
            </div>
          </Box>
        </Modal>
      </Main>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    token: state.admin.token,
    role: state.admin.role,
  };
};

export default connect(mapStateToProps, null)(BulkUploadSubmission);
